import React from "react";
import { MapPin, Phone, MessageCircle, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { CONTACT, buildWhatsAppLink } from "@/lib/contact";
import Reveal from "@/components/Reveal";

export const Location = () => {
  const { t } = useLang();
  const waLink = buildWhatsAppLink(t.whatsappMessage);

  return (
    <section
      id="location"
      data-testid="location-section"
      className="section-pad bg-[#FDFBF7] relative"
    >
      <div
        aria-hidden
        className="absolute inset-x-0 top-0 h-[3px] bg-[#003366]/90"
      />
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <Reveal className="lg:col-span-5">
            <span className="ornament text-[#E2725B]">
              {t.location.overline}
            </span>
            <h2
              data-testid="location-title"
              className="mt-6 font-serif text-[#003366] text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight whitespace-pre-line"
            >
              {t.location.title}
            </h2>

            {/* Prominent "In Vilamoura" button */}
            <a
              href={CONTACT.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-in-vilamoura-btn"
              className="mt-8 inline-flex items-center gap-2 bg-[#003366] text-white hover:bg-[#002247] transition-colors px-5 py-3 text-sm font-semibold"
            >
              <span aria-hidden>📍</span>
              {t.inVilamoura}
              <ExternalLink className="h-3.5 w-3.5 opacity-80" />
            </a>

            {/* Address block */}
            <a
              href={CONTACT.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="location-address-link"
              className="mt-10 flex items-start gap-4 group"
            >
              <MapPin className="h-5 w-5 text-[#D4AF37] mt-1 shrink-0" />
              <div>
                <div className="font-serif text-xl text-[#003366] group-hover:text-[#E2725B] transition-colors">
                  Monte Sol Stone Steak House
                </div>
                <div className="text-sm text-[#2D3748] mt-1 leading-relaxed">
                  {CONTACT.addressLine}
                  <br />
                  {CONTACT.addressPostal}
                </div>
              </div>
            </a>

            {/* Hours block */}
            <div className="mt-8 border-t border-[#708090]/30 pt-6">
              <div className="text-[10px] tracking-[0.3em] uppercase font-semibold text-[#708090]">
                {t.location.hoursTitle}
              </div>
              <div className="mt-3 space-y-1">
                <div className="text-[#2D3748]">{t.location.hoursLine1}</div>
                <div className="text-[#708090] text-sm">
                  {t.location.hoursLine2}
                </div>
                <div className="text-[#E2725B] font-semibold text-sm">
                  {t.location.hoursLine3}
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <a
                href={CONTACT.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-maps-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#0047AB] text-white hover:bg-[#003380] transition-colors px-6 py-3 uppercase tracking-[0.2em] text-[10px] font-semibold"
              >
                <MapPin className="h-3.5 w-3.5" />
                {t.location.openMaps}
                <ExternalLink className="h-3 w-3 opacity-70" />
              </a>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="location-whatsapp-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-white hover:bg-[#b5952f] transition-colors px-6 py-3 uppercase tracking-[0.2em] text-[10px] font-semibold"
              >
                <MessageCircle className="h-3.5 w-3.5" />
                {t.location.whatsapp}
              </a>
              <a
                href={`tel:${CONTACT.phone}`}
                data-testid="location-call-btn"
                className="inline-flex items-center justify-center gap-2 border border-[#E2725B] text-[#E2725B] hover:bg-[#E2725B] hover:text-white transition-colors px-6 py-3 uppercase tracking-[0.2em] text-[10px] font-semibold"
              >
                <Phone className="h-3.5 w-3.5" />
                {t.location.callUs}
              </a>
            </div>
          </Reveal>

          {/* Map */}
          <Reveal delay={150} className="lg:col-span-7 relative">
            <div className="aspect-[4/3] md:aspect-[5/4] w-full border border-[#708090]/25 overflow-hidden bg-[#F9F9F9]">
              <iframe
                title="Monte Sol map"
                src={CONTACT.mapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
                data-testid="location-map-iframe"
              />
            </div>
            <div className="absolute -top-3 -left-3 bg-[#D4AF37] text-[#003366] px-4 py-2 text-[10px] tracking-[0.25em] uppercase font-bold">
              Vilamoura · Algarve
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
